













































































































































































import Vue, { PropType } from "vue";
import { computed, ref, watch } from "@vue/composition-api";
import SkeletonLoaderItem from "@/components/SkeletonLoaderItem.vue";
import GaIcon from "../../card-items/GaIcon.vue";
import { useGenAiPalette } from "../../data/design";
import GaEventRowMenu from "./GaEventRowMenu.vue";
import GaRiskBadge from "./GaRiskBadge.vue";
import GaSensitivityBadge from "./GaSensitivityBadge.vue";
import { EventView, department_line } from "./event-view";

export default Vue.extend({
  components: { GaIcon, GaEventRowMenu, GaRiskBadge, GaSensitivityBadge, SkeletonLoaderItem },
  props: {
    events: { type: Array as PropType<EventView[]>, required: true },
    loading: { type: Boolean, default: false },
    has_more: { type: Boolean, default: false },
    /**
     * The Activity Type column. An employee's own AI interactions (task
     * 25091) leave it out: that list is already scoped to them.
     */
    show_activity: { type: Boolean, default: true },
    /** the Date column; a content item has no date of its own */
    show_date: { type: Boolean, default: true },
    /** a failed request, said in place of rows (or under the ones loaded) */
    error: { type: String, default: "" },
    /** what an empty list says */
    empty_text: { type: String, default: "No events match the selected filters." },
  },
  setup(props, { emit }) {
    const T = useGenAiPalette();
    const scroller = ref<HTMLElement | null>(null);

    // a new filter starts the list from the top
    watch(
      () => (props.events as EventView[]).length === 0,
      (empty) => {
        if (empty && scroller.value) scroller.value.scrollTop = 0;
      },
      { lazy: true }
    );

    const on_scroll = (e: Event) => {
      const el = e.currentTarget as HTMLElement;
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 200 &&
        props.has_more &&
        !props.loading
      ) {
        emit("more");
      }
    };

    const headers = computed(
      () =>
        [
          { label: "" },
          ...(props.show_activity ? [{ label: "Activity Type" }] : []),
          { label: "User" },
          { label: "Content" },
          { label: "AI Agent" },
          ...(props.show_date ? [{ label: "Date" }] : []),
          { label: "Risk", center: true },
          { label: "Sensitivity", center: true },
          { label: "" },
        ] as { label: string; center?: boolean }[]
    );

    /**
     * Column widths, as shares of the table: Content gets the most room, the
     * Activity Type no more than its label needs (Ami, Oct 2026). Without the
     * Activity Type column (an employee's own list), its share goes mostly to
     * Content, so the columns still fill the row.
     */
    const widths = computed(() => {
      const w = props.show_activity
        ? { activity: 11, user: 14, content: 30, agent: 12, date: 10, risk: 5, sensitivity: 8 }
        : { activity: 0, user: 16, content: 36, agent: 12, date: 12, risk: 6, sensitivity: 8 };
      if (!props.show_date) w.content += w.date;
      const pct = (n: number) => n + "%";
      return {
        activity: pct(w.activity),
        user: pct(w.user),
        content: pct(w.content),
        agent: pct(w.agent),
        date: pct(w.date),
        risk: pct(w.risk),
        sensitivity: pct(w.sensitivity),
      };
    });

    const head = computed(() => ({
      position: "sticky",
      top: 0,
      zIndex: 20,
      padding: "8px 12px",
      fontSize: "12px",
      fontWeight: 600,
      color: T.value.ev.strong,
      background: T.value.ev.bg,
      boxShadow: "inset 0 -1px 0 0 " + T.value.ev.headRule,
    }));

    const primary = computed(() => ({
      fontSize: "12px",
      fontWeight: 600,
      color: T.value.ev.title,
      lineHeight: 1.25,
    }));
    const secondary = computed(() => ({
      fontSize: "10.5px",
      color: T.value.ev.muted,
      lineHeight: 1.25,
    }));

    return {
      T,
      scroller,
      on_scroll,
      head,
      primary,
      secondary,
      department: (e: EventView) => department_line(e.user),
      is_event_match: (e: EventView) => !!e.match && e.match.level === "event",
      // the chip counts the event's files, which is not the number that matched
      extra_title: (e: EventView) =>
        (e.match && e.match.level === "event"
          ? `${e.content.files.length} files in this event; the list does not say which matched ${e.match.relation}.\n`
          : "") + e.content.files.join("\n"),
      is_missing: (e: EventView, field: string) => (e.missing || []).includes(field as any),
      headers,
      widths,
      columns: computed(() => headers.value.length),
    };
  },
});
